import Page from "components/page/Page";
import React, { useCallback, useEffect } from "react";
import { useHistory, useParams } from "react-router";
import WebsiteBuilderTemplate from "./WebsiteBuilderTemplate";
import { useWebsite } from "../shared/hooks/useWebsite";
import { WebsiteBuilderLoadingContent } from "./components/loadingContent/WebsiteBuilderLoadingContent";
import { GetWebsiteBuilderTemplate, PostWebsiteBuilderWebsite, ReapplyWebsiteBuilderTemplate } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Toggle from "components/form/toggle/Toggle";
import "./websiteBuilderTemplatePreview.scss";
import { IWebsiteBuilderTemplate } from "redux/reducers/models/websiteBuilder";
import { useWebsiteBuilderLoadingState } from "../shared/hooks/useWebsiteBuilderLoadingState";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

interface IParams {
  templateId: string;
}

export default function WebsiteBuilderTemplatePreview() {
  const { t } = useTranslation();
  
  const dispatch = useAppDispatch();

  const history = useHistory();

  const { templateId } = useParams<IParams>();

  const { state: switchTemplateModal, updateModal: updateSwitchTemplateModal, closeModal: closeSwitchTemplateModal } = useModal({
    isOpen: false,
    preserveContent: true,
    clearOnClose: true,
  });

  const {
    website,
    websiteExists,
    websiteLoadingStatus,
    setWebsite,
  } = useWebsite({ websiteMustExist: false });

  const {
    state: template,
    stateExists: templateExists,
    stateLoadingStatus: templateLoadingStatus,
    loadInProgress: templateLoadInProgress,
    loadFailed: templateLoadFailed,
    loadSucceeded: templateLoadSucceeded,
  } = useWebsiteBuilderLoadingState<IWebsiteBuilderTemplate>({
    state: null,
    stateExists: false,
    stateLoadingStatus: "loading",
  });

  const loadTemplate = useCallback(async (token: CancelToken) => {
    templateLoadInProgress();

    const res = await GetWebsiteBuilderTemplate({ id: Number(templateId) }, false, token);

    if (res.status !== StatusCode.OK) {
      if (!token.reason) {
        templateLoadFailed();
        dispatch(showError(typeof res.data === "string" ? res.data : t("Error loading template")));
      }

      return;
    }

    templateLoadSucceeded(res.data, res.data != null);
  }, [templateId]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadTemplate(source.token);

    return () => source.cancel();
  }, [loadTemplate]);

  function renderTemplatePreview() {
    if (websiteLoadingStatus === "succeeded" && templateLoadingStatus === "succeeded" && templateExists) {
      return <WebsiteBuilderTemplate template={template} />;
    } else {
      return null;
    }
  }

  async function handleSelectTemplate() {
    if (!websiteExists) {
      const res = await PostWebsiteBuilderWebsite({
        template_id: template.id,
      }, true);

      if (res.status !== StatusCode.OK) {
        dispatch(showError(typeof res.data === "string" ? res.data : t("Error creating website")));
        return;
      }

      setWebsite(res.data);

      history.push("/admin/website-builder");
    } else {
      updateSwitchTemplateModal({ isOpen: true });
    }
  }

  async function handleSwitchTemplate() {
    const res = await ReapplyWebsiteBuilderTemplate({
      website_id: website.id,
      template_id: Number(templateId),
      preserve_content: switchTemplateModal.preserveContent,
    }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error switching template")));
      return;
    }

    setWebsite(res.data);

    dispatch(showSuccess(t("Successfully switched template")));

    history.push("/admin/website-builder");
  }

  return (
    <Page
      narrow
      title={t("Preview")}
      subtitle={t("You can customize the layout, colours, fonts, or theme when editing")}
      primaryAction={{
        content: t("Select template"),
        action: handleSelectTemplate,
        disabled: websiteLoadingStatus !== "succeeded" || templateLoadingStatus !== "succeeded" || !templateExists,
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Go back"),
          url: "/admin/website-builder/templates",
        },
      ]}
    >
      <WebsiteBuilderLoadingContent loading={websiteLoadingStatus === "standby" || websiteLoadingStatus === "loading" || templateLoadingStatus === "loading"}>
        {renderTemplatePreview()}
      </WebsiteBuilderLoadingContent>
      <Portal isMounted={switchTemplateModal.isOpen}>
        <Sheet
          title={t("Switch template?")}
          open={switchTemplateModal.isOpen}
          size="small"
          closable
          onCancel={closeSwitchTemplateModal}
          onOk={handleSwitchTemplate}
          okText={t("Yes, switch")}
        >
          <div className="website-builder-switch-template-modal">
            <p>{t("Changing your template will update the style and layout of your website. Your website content will remain.")}</p>
            <p>{t("Do you wish to proceed?")}</p>
            <hr />
            <h3 className="switch-template-modal-title">{t("Settings")}</h3>
            <Toggle
              size="small"
              labelRight={t("Preserve content. Keep all saved content on your website.")}
              onChange={() => updateSwitchTemplateModal({ preserveContent: !switchTemplateModal.preserveContent })}
              checked={switchTemplateModal.preserveContent}
            />
          </div>
        </Sheet>
      </Portal>
    </Page>
  );
}
